import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Radio, Table, App, Space, Typography, Divider } from 'antd';
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDateTimeEuropean } from '../utils/dateUtils';

const { Option } = Select;
const { Title, Text } = Typography;

interface ExportHistory {
  id: string;
  year: number;
  month: number;
  type: string;
  recordCount: number;
  exportedAt: string;
}

const IntrastatExport: React.FC = () => {
  const { t } = useTranslation(['reports', 'common']);
  const { message } = App.useApp();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [type, setType] = useState<'ARRIVAL' | 'DISPATCH'>('ARRIVAL');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<ExportHistory[]>([]);

  const months = [
    { value: 1, label: t('reports:intrastat.months.january') },
    { value: 2, label: t('reports:intrastat.months.february') },
    { value: 3, label: t('reports:intrastat.months.march') },
    { value: 4, label: t('reports:intrastat.months.april') },
    { value: 5, label: t('reports:intrastat.months.may') },
    { value: 6, label: t('reports:intrastat.months.june') },
    { value: 7, label: t('reports:intrastat.months.july') },
    { value: 8, label: t('reports:intrastat.months.august') },
    { value: 9, label: t('reports:intrastat.months.september') },
    { value: 10, label: t('reports:intrastat.months.october') },
    { value: 11, label: t('reports:intrastat.months.november') },
    { value: 12, label: t('reports:intrastat.months.december') }
  ];

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await api.get('/intrastat/history');
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching export history:', error);
      message.error(t('reports:messages.historyFailed'));
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await api.post(
        '/intrastat/export',
        { year, month, type },
        {
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `INTRASTAT_${type}_${year}_${String(month).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t('reports:messages.exportSuccess'));
      fetchHistory(); // Refresh history
    } catch (error) {
      console.error('Error exporting INTRASTAT:', error);
      message.error(t('reports:messages.exportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = [
    {
      title: t('reports:intrastat.table.date'),
      dataIndex: 'exportedAt',
      key: 'exportedAt',
      render: (date: string) => formatDateTimeEuropean(date)
    },
    {
      title: t('reports:intrastat.year'),
      dataIndex: 'year',
      key: 'year'
    },
    {
      title: t('reports:intrastat.month'),
      dataIndex: 'month',
      key: 'month',
      render: (month: number) => months.find(m => m.value === month)?.label
    },
    {
      title: t('reports:intrastat.table.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => type === 'ARRIVAL' ? t('reports:intrastat.arrivals').split(' ')[0] : t('reports:intrastat.dispatches').split(' ')[0]
    },
    {
      title: t('reports:intrastat.table.recordCount'),
      dataIndex: 'recordCount',
      key: 'recordCount'
    }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div>
      <Card 
        title={
          <Space>
            <FileExcelOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
            <Title level={4} style={{ margin: 0 }}>{t('reports:intrastat.title')}</Title>
          </Space>
        }
        style={{ marginBottom: 20 }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('reports:intrastat.selectParameters')}</Text>
          </div>
          
          <Space size="large" wrap>
            <div>
              <Text>{t('reports:intrastat.year')}:</Text>
              <Select 
                value={year} 
                onChange={setYear}
                style={{ width: 120, marginLeft: 10 }}
              >
                {years.map(y => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </div>

            <div>
              <Text>{t('reports:intrastat.month')}:</Text>
              <Select 
                value={month} 
                onChange={setMonth}
                style={{ width: 150, marginLeft: 10 }}
              >
                {months.map(m => (
                  <Option key={m.value} value={m.value}>{m.label}</Option>
                ))}
              </Select>
            </div>

            <div>
              <Text>{t('reports:intrastat.declarationType')}:</Text>
              <Radio.Group 
                value={type} 
                onChange={(e) => setType(e.target.value)}
                style={{ marginLeft: 10 }}
              >
                <Radio value="ARRIVAL">{t('reports:intrastat.arrivals')}</Radio>
                <Radio value="DISPATCH">{t('reports:intrastat.dispatches')}</Radio>
              </Radio.Group>
            </div>
          </Space>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              size="large"
              loading={loading}
              onClick={handleExport}
              style={{ minWidth: 200 }}
            >
              {t('reports:intrastat.generateExport')}
            </Button>
          </div>

          <div style={{ marginTop: 10 }}>
            <Text type="secondary">
              {t('reports:intrastat.info.description')}
            </Text>
          </div>
        </Space>
      </Card>

      {history.length > 0 && (
        <Card title={t('reports:intrastat.exportHistory')}>
          <Table 
            dataSource={history}
            columns={historyColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: t('reports:intrastat.noHistory')
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default IntrastatExport;